import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Avatar,
  Stack,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Fab,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Visibility as VisibilityIcon,
  CardGiftcard as CardGiftcardIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  LocalOffer as OfferIcon,
  Store as StoreIcon,
  QrCode as QrCodeIcon,
  Code as CodeIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Assignment as AssignmentIcon,
  CheckCircleOutline,
  HourglassEmpty,
  Block,
  Settings as ProcessingIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  AdminPanelSettings as AdminIcon,
  Support as SupportIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../services/api';

interface Redemption {
  id: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  benefit_id: string;
  benefit_name: string;
  benefit_description: string;
  company_name: string;
  benefit_type: string;
  redemption_code: string;
  status: 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'expired';
  redemption_type: string;
  redemption_value: number;
  original_value: number;
  discount_percentage: number;
  redemption_method: string;
  promo_code?: string;
  qr_code?: string;
  redeemed_at?: string;
  approved_at?: string;
  completed_at?: string;
  expires_at?: string;
  notes?: string;
  admin_notes?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

interface ChatMessage {
  id: string;
  redemption_id: string;
  sender_type: 'user' | 'admin';
  sender_name: string;
  message: string;
  timestamp: string;
  is_read: boolean;
}

interface FilterState {
  status: string;
  benefitType: string;
  redemptionMethod: string;
  dateRange: string;
  searchTerm: string;
  company: string;
}

const RedemptionsWithChat: React.FC = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRedemption, setSelectedRedemption] = useState<Redemption | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [chatDialog, setChatDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [companies, setCompanies] = useState<string[]>([]);
  const [benefitTypes, setBenefitTypes] = useState<string[]>([]);
  
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    benefitType: 'all',
    redemptionMethod: 'all',
    dateRange: 'all',
    searchTerm: '',
    company: 'all',
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRedemptions();
  }, [filters]);

  const fetchRedemptions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/admin/redemptions');
      const data = response.data;
      setRedemptions(Array.isArray(data) ? data : data.redemptions || []);
      
      // Extract unique companies and benefit types
      const uniqueCompanies = Array.from(new Set(data.map((r: Redemption) => r.company_name).filter(Boolean))) as string[];
      const uniqueTypes = Array.from(new Set(data.map((r: Redemption) => r.benefit_type).filter(Boolean))) as string[];
      
      setCompanies(uniqueCompanies);
      setBenefitTypes(uniqueTypes);
    } catch (err: any) {
      console.error('Error fetching redemptions:', err);
      setError('Failed to load redemptions');
      setRedemptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatMessages = async (redemptionId: string) => {
    try {
      const response = await api.get(`/admin/redemptions/${redemptionId}/chat`);
      setChatMessages(response.data || []);
    } catch (err) {
      console.error('Error fetching chat messages:', err);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedRedemption) return;

    try {
      setSendingMessage(true);
      const response = await api.post(`/admin/redemptions/${selectedRedemption.id}/chat`, {
        message: newMessage,
        sender_type: 'admin',
        sender_name: 'Support Admin'
      });

      if (response.data?.message) {
        setChatMessages(prev => [...prev, response.data.message]);
        setNewMessage('');
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  const getFilteredRedemptions = () => {
    let filtered = [...redemptions];
    
    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(r => r.status === filters.status);
    }
    
    // Benefit type filter
    if (filters.benefitType !== 'all') {
      filtered = filtered.filter(r => r.benefit_type === filters.benefitType);
    }
    
    // Company filter
    if (filters.company !== 'all') {
      filtered = filtered.filter(r => r.company_name === filters.company);
    }
    
    // Redemption method filter
    if (filters.redemptionMethod !== 'all') {
      filtered = filtered.filter(r => r.redemption_method === filters.redemptionMethod);
    }
    
    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const dateFilters: { [key: string]: (r: Redemption) => boolean } = {
        today: (r: Redemption) => {
          const today = new Date().toDateString();
          return new Date(r.created_at).toDateString() === today;
        },
        week: (r: Redemption) => {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return new Date(r.created_at) >= weekAgo;
        },
        month: (r: Redemption) => {
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return new Date(r.created_at) >= monthAgo;
        },
      };
      
      if (dateFilters[filters.dateRange]) {
        filtered = filtered.filter(dateFilters[filters.dateRange]);
      }
    }
    
    // Search filter
    if (filters.searchTerm) {
      const search = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.user_first_name?.toLowerCase().includes(search) ||
        r.user_last_name?.toLowerCase().includes(search) ||
        r.user_email?.toLowerCase().includes(search) ||
        r.benefit_name?.toLowerCase().includes(search) ||
        r.redemption_code?.toLowerCase().includes(search) ||
        r.company_name?.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  };

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      await api.put(`/admin/redemptions/${id}`, {
        status: newStatus,
        admin_notes: actionNotes,
      });

      // Update local state
      setRedemptions(prev => prev.map(r => 
        r.id === id 
          ? { ...r, status: newStatus as any, admin_notes: actionNotes }
          : r
      ));
      
      setActionDialog(false);
      setActionNotes('');
      setSelectedRedemption(null);
    } catch (err: any) {
      setError('Failed to update redemption status');
    }
  };

  const openChat = async (redemption: Redemption) => {
    setSelectedRedemption(redemption);
    setChatDialog(true);
    await fetchChatMessages(redemption.id);
  };

  const getStatusIcon = (status: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      pending: <HourglassEmpty />,
      approved: <CheckCircleOutline />,
      processing: <ProcessingIcon />,
      completed: <CheckCircleIcon />,
      rejected: <Block />,
      expired: <ScheduleIcon />,
    };
    return icons[status] || <PendingIcon />;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: any } = {
      pending: 'warning',
      approved: 'info',
      processing: 'default',
      completed: 'success',
      rejected: 'error',
      expired: 'default',
    };
    return colors[status] || 'default';
  };

  const getMethodIcon = (method: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      'online': <BusinessIcon />,
      'in-store': <StoreIcon />,
      'promo-code': <CodeIcon />,
      'qr-code': <QrCodeIcon />,
    };
    return icons[method] || <CardGiftcardIcon />;
  };

  const stats = {
    total: redemptions.length,
    pending: redemptions.filter(r => r.status === 'pending').length,
    approved: redemptions.filter(r => r.status === 'approved').length,
    processing: redemptions.filter(r => r.status === 'processing').length,
    completed: redemptions.filter(r => r.status === 'completed').length,
    totalValue: redemptions.reduce((sum, r) => sum + (r.redemption_value || 0), 0),
    totalSavings: redemptions.reduce((sum, r) => sum + (r.original_value - r.redemption_value || 0), 0),
  };

  const filteredRedemptions = getFilteredRedemptions();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Redemptions Management with Chat
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track and manage all benefit redemptions with real-time communication
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    Total Redemptions
                  </Typography>
                  <Typography variant="h4">
                    {stats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <CardGiftcardIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    Pending Approval
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <PendingIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    Total Value
                  </Typography>
                  <Typography variant="h5">
                    R{stats.totalValue.toLocaleString()}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <MoneyIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    User Savings
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    R{stats.totalSavings.toLocaleString()}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? "contained" : "outlined"}
              size="small"
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </Stack>

          {/* Search Bar */}
          <TextField
            placeholder="Search by user, benefit, code, or company..."
            value={filters.searchTerm}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            fullWidth
            sx={{ mb: showFilters ? 2 : 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {showFilters && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Benefit Type</InputLabel>
                  <Select
                    value={filters.benefitType}
                    onChange={(e) => setFilters({ ...filters, benefitType: e.target.value })}
                    label="Benefit Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    {benefitTypes.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={filters.company}
                    onChange={(e) => setFilters({ ...filters, company: e.target.value })}
                    label="Company"
                  >
                    <MenuItem value="all">All Companies</MenuItem>
                    {companies.map(company => (
                      <MenuItem key={company} value={company}>{company}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={filters.dateRange}
                    onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                    label="Date Range"
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredRedemptions.length} of {redemptions.length} redemptions
        </Typography>
      </Box>

      {/* Redemptions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Benefit</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRedemptions.map((redemption) => (
              <TableRow key={redemption.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {redemption.redemption_code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack>
                    <Typography variant="body2">
                      {redemption.user_first_name} {redemption.user_last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {redemption.user_email}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack>
                    <Typography variant="body2">
                      {redemption.benefit_name}
                    </Typography>
                    <Chip 
                      label={redemption.benefit_type} 
                      size="small" 
                      variant="outlined"
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {redemption.company_name}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack>
                    <Typography variant="body2" fontWeight="bold">
                      R{redemption.redemption_value?.toLocaleString()}
                    </Typography>
                    {redemption.discount_percentage > 0 && (
                      <Chip 
                        label={`-${redemption.discount_percentage}%`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getMethodIcon(redemption.redemption_method)}
                    <Typography variant="caption">
                      {redemption.redemption_method?.replace('-', ' ')}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(redemption.created_at).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(redemption.created_at).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(redemption.status)}
                    label={redemption.status}
                    size="small"
                    color={getStatusColor(redemption.status)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedRedemption(redemption);
                          setDetailsDialog(true);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open Chat">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openChat(redemption)}
                      >
                        <ChatIcon />
                      </IconButton>
                    </Tooltip>
                    {redemption.status === 'pending' && (
                      <Tooltip title="Take Action">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => {
                            setSelectedRedemption(redemption);
                            setActionDialog(true);
                          }}
                        >
                          <AssignmentIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Chat Dialog */}
      <Dialog
        open={chatDialog}
        onClose={() => setChatDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <AppBar position="static" color="primary">
          <Toolbar>
            <ChatIcon sx={{ mr: 2 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">
                Chat: {selectedRedemption?.redemption_code}
              </Typography>
              <Typography variant="caption">
                {selectedRedemption?.user_first_name} {selectedRedemption?.user_last_name}
              </Typography>
            </Box>
            <IconButton
              color="inherit"
              onClick={() => setChatDialog(false)}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Chat Messages */}
          <Box sx={{ 
            flexGrow: 1, 
            p: 2, 
            overflowY: 'auto',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          }}>
            <List>
              {chatMessages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender_type === 'admin' ? 'flex-end' : 'flex-start',
                    mb: 1
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: message.sender_type === 'admin' ? 'primary.main' : 'background.paper',
                      color: message.sender_type === 'admin' ? 'primary.contrastText' : 'text.primary'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Avatar sx={{ 
                        width: 24, 
                        height: 24,
                        bgcolor: message.sender_type === 'admin' ? 'secondary.main' : 'grey.400'
                      }}>
                        {message.sender_type === 'admin' ? <AdminIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                      </Avatar>
                      <Typography variant="caption" fontWeight="bold">
                        {message.sender_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </Typography>
                    </Stack>
                    <Typography variant="body2">
                      {message.message}
                    </Typography>
                  </Paper>
                </ListItem>
              ))}
            </List>
          </Box>
          
          {/* Message Input */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={sendChatMessage}
                disabled={!newMessage.trim() || sendingMessage}
                sx={{ minWidth: '120px' }}
              >
                {sendingMessage ? <CircularProgress size={20} /> : 'Send'}
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Details Dialog (same as before) */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Redemption Details</Typography>
            <Chip
              icon={getStatusIcon(selectedRedemption?.status || '')}
              label={selectedRedemption?.status}
              color={getStatusColor(selectedRedemption?.status || '')}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRedemption && (
            <Grid container spacing={3}>
              {/* User Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  User Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body2">
                            {selectedRedemption.user_first_name} {selectedRedemption.user_last_name}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EmailIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body2">
                            {selectedRedemption.user_email}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Benefit Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Benefit Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Benefit Name
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedRedemption.benefit_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedRedemption.benefit_description}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Company
                      </Typography>
                      <Typography variant="body2">
                        {selectedRedemption.company_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <Chip label={selectedRedemption.benefit_type} size="small" />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Notes */}
              {(selectedRedemption.notes || selectedRedemption.admin_notes) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Notes
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {selectedRedemption.notes && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          User Notes
                        </Typography>
                        <Typography variant="body2">
                          {selectedRedemption.notes}
                        </Typography>
                      </Box>
                    )}
                    {selectedRedemption.admin_notes && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Admin Notes
                        </Typography>
                        <Typography variant="body2">
                          {selectedRedemption.admin_notes}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<ChatIcon />}
            onClick={() => {
              setDetailsDialog(false);
              if (selectedRedemption) openChat(selectedRedemption);
            }}
          >
            Open Chat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog}
        onClose={() => setActionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Process Redemption
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Redemption: <strong>{selectedRedemption?.redemption_code}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              User: <strong>{selectedRedemption?.user_first_name} {selectedRedemption?.user_last_name}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Benefit: <strong>{selectedRedemption?.benefit_name}</strong>
            </Typography>
            
            <TextField
              label="Admin Notes"
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 3 }}
              placeholder="Add any notes about this action..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleUpdateStatus(selectedRedemption!.id, 'rejected')}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleUpdateStatus(selectedRedemption!.id, 'approved')}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RedemptionsWithChat;